import type { BackendFieldType, FieldInputPayload, FormField, FormFieldType } from '../types';

const PHONE_VALIDATION_JSON = '{"pattern":"phone"}';
const CURRENCY_VALIDATION_JSON = '{"format":"currency"}';

export interface LinearScaleOptions {
  min: number;
  max: number;
  minLabel?: string;
  maxLabel?: string;
}

export interface GridOptions {
  rows: string[];
  columns: string[];
}

/** Backend requires `options`/`validation` as valid JSON (or null) — never free text. */
function parseJson<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

/** `Select`/`MultiSelect` — `options` is a JSON array string, e.g. `'["A","B"]'`. */
export function parseChoiceOptions(options: string | null): string[] {
  return parseJson<string[]>(options) ?? [];
}

export function serializeChoiceOptions(values: string[]): string {
  return JSON.stringify(values);
}

const INVARIANT_NUMBER_REGEX = /^[+-]?(\d{1,3}(,\d{3})*|\d+)(\.\d+)?$/;

/**
 * `Number`/`Currency` — mirrors the backend's invariant-culture decimal parsing:
 * `.` as decimal separator, optional `,` thousands grouping, optional leading `+`/`-`.
 * A comma used as a decimal separator (e.g. `"3,5"`) is intentionally rejected.
 */
export function isValidInvariantNumber(value: string): boolean {
  return INVARIANT_NUMBER_REGEX.test(value.trim());
}

/** `LinearScale` — `options` is a JSON object, e.g. `'{"min":1,"max":5}'`. */
export function parseLinearScaleOptions(options: string | null): LinearScaleOptions {
  return parseJson<LinearScaleOptions>(options) ?? { min: 1, max: 5 };
}

export function serializeLinearScaleOptions(value: LinearScaleOptions): string {
  return JSON.stringify(value);
}

/** `Grid` — `options` is a JSON object, e.g. `'{"rows":["R1"],"columns":["C1"]}'`. */
export function parseGridOptions(options: string | null): GridOptions {
  return parseJson<GridOptions>(options) ?? { rows: [], columns: [] };
}

export function serializeGridOptions(value: GridOptions): string {
  return JSON.stringify(value);
}

/** Converts a builder field (frontend-only types included) into the wire shape the backend accepts. */
export function toFieldInputPayload(field: FormField): FieldInputPayload {
  if (field.fieldType === 'Phone') {
    return {
      fieldType: 'Text',
      label: field.label,
      required: field.required,
      order: field.order,
      options: null,
      validation: PHONE_VALIDATION_JSON,
    };
  }
  if (field.fieldType === 'Currency') {
    return {
      fieldType: 'Number',
      label: field.label,
      required: field.required,
      order: field.order,
      options: null,
      validation: CURRENCY_VALIDATION_JSON,
    };
  }
  return {
    fieldType: field.fieldType as BackendFieldType,
    label: field.label,
    required: field.required,
    order: field.order,
    options: field.options,
    validation: field.validation,
  };
}

/** Recovers the frontend-only type (Phone/Currency) from the backend's `fieldType` + `validation` convention. */
export function toFrontendFieldType(
  fieldType: BackendFieldType,
  validation: string | null,
): FormFieldType {
  if (fieldType === 'Text' && parseJson<{ pattern?: string }>(validation)?.pattern === 'phone') {
    return 'Phone';
  }
  if (fieldType === 'Number' && parseJson<{ format?: string }>(validation)?.format === 'currency') {
    return 'Currency';
  }
  return fieldType;
}

export function fromFormDetailField(field: FormField): FormField {
  return {
    ...field,
    fieldType: toFrontendFieldType(field.fieldType as BackendFieldType, field.validation),
  };
}

/** Wire shape of `FormTemplateDetailDto.fieldDefinitions` — a JSON-encoded array with PascalCase keys. */
interface FieldDefinitionWire {
  Id: number | string | null;
  FieldType: BackendFieldType;
  Label: string;
  Required: boolean;
  Order: number;
  Options: string | null;
  Validation: string | null;
}

/** Parses `GET /templates/{id}`'s `fieldDefinitions` JSON string into builder-ready `FormField[]`. */
export function parseFieldDefinitions(json: string): FormField[] {
  const parsed = parseJson<FieldDefinitionWire[]>(json) ?? [];
  return parsed.map((f) => ({
    id: f.Id === null || f.Id === undefined ? null : String(f.Id),
    fieldType: f.FieldType,
    label: f.Label,
    required: f.Required,
    order: f.Order,
    options: f.Options ?? null,
    validation: f.Validation ?? null,
  }));
}
